import { useState, useCallback, useMemo, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getCompanyOvertimeThreshold } from "@/utils/overtimeUtils";
import { validateTimeDuration, validateOvertimeShiftOverlap } from "@/utils/dateTimeHelpers";
import { useEmployeeShiftData } from "@/hooks/useEmployeeShiftData";
import { getShiftDetailsForDay } from "@/utils/shiftFormUtils";
import { User, Shift, OvertimeRequest, DayTimeDeduction } from "@/types";
import { toast as sonnerToast } from "sonner";
import { OvertimeStatus, UserRole } from "@/types/enums";
import { getApprovedOvertimeHoursForDate } from "@/services/employeeOvertimeService";
import { type Holiday } from "@/utils/holidayUtils";
import { v4 as uuidv4 } from 'uuid';

import { useAuth } from "@/contexts/auth/useAuth";
import { useQuery } from "@tanstack/react-query";
import { useDailyOvertimeRecords } from "@/hooks/useQueryHooks";
import { fetchActiveEmployeeShift } from "@/services/employeeShiftService";
import { useOvertime } from "@/contexts/overtime/useOvertime";

interface OvertimeReason {
  id: string;
  code: string;
  main_title: string;
  reason: string;
  date_created: string;
}

interface UseOvertimeRequestFormProps {
  onSuccess?: () => void;
  companyId?: string | null;
  selectedShiftData?: Shift | null;
}

export function useOvertimeRequestForm({ onSuccess, companyId, selectedShiftData }: UseOvertimeRequestFormProps = {}) {
  // Form state
  const [selectedEmployee, setSelectedEmployee] = useState<User | null>(null);
  const [fromDate, setFromDate] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [justification, setJustification] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [confirmationError, setConfirmationError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [pendingSubmitEvent, setPendingSubmitEvent] = useState<React.FormEvent | null>(null);
  const [dailyOvertimeError, setDailyOvertimeError] = useState<string | null>(null);
  const [overtimeReasons, setOvertimeReasons] = useState<OvertimeReason[]>([]);
  const [employeeBreakHours, setEmployeeBreakHours] = useState<number | null>(null);
  const [willDeductBreak, setWillDeductBreak] = useState(false);
  const [breakDeductionNotice, setBreakDeductionNotice] = useState<string | null>(null);
  
  // Break deduction confirmation modal state
  const [showBreakDeductionModal, setShowBreakDeductionModal] = useState(false);
  const [breakDeductionData, setBreakDeductionData] = useState<{
    originalHours: number;
    breakHours: number;
    finalHours: number;
    approvedHours: number;
  } | null>(null);
  
  // Holiday state
  const [holidayInfo, setHolidayInfo] = useState<Holiday | null>(null);
  const [isHoliday, setIsHoliday] = useState(false);
  
  // Company holidays via React Query (prefetched on login)
  const { data: companyHolidays = [] } = useQuery({
    queryKey: ['companyHolidays', companyId],
    queryFn: async () => {
      if (!companyId) return [] as Holiday[];
      const { data, error } = await supabase
        .from('holidays')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_public', true);
      if (error) {
        return [] as Holiday[];
      }
      return (data || []) as Holiday[];
    },
    enabled: !!companyId,
    staleTime: 60 * 60 * 1000,
  });
  
  // Get current user from auth context
  const { user } = useAuth();
  
  // ✅ NEW: Get instant state update functions from overtime context
  const { 
    addOvertimeRequestToState, 
    updateOvertimeRequestInState, 
    removeOvertimeRequestFromState
  } = useOvertime();

  // Fetch company threshold
  const { data: companyThreshold } = useQuery({
    queryKey: ['companyThreshold', companyId],
    queryFn: async () => {
      if (!companyId) return null;
      return getCompanyOvertimeThreshold(companyId);
    },
    enabled: !!companyId
  });

  // Fetch overtime reasons for label lookup
  useEffect(() => {
    const fetchReasons = async () => {
      try {
        if (!companyId) return;
        
        const { data, error } = await supabase
          .from('overtime_reasons')
          .select('*')
          .eq('company_id', companyId)
          .eq('is_active', true)
          .order('main_title', { ascending: true })
          .order('reason', { ascending: true });
        if (!error && data) {
          setOvertimeReasons(data);
        }
      } catch {
        // Error handling silently fails here, could be improved
      }
    };
    fetchReasons();
  }, [companyId]);

  // Fetch employee's shift break hours when employee and date are selected
  useEffect(() => {
    const fetchEmployeeBreakHours = async () => {
      if (!selectedEmployee?.id || !fromDate) {
        setEmployeeBreakHours(null);
        return;
      }
      
      try {
        const shiftData = await fetchActiveEmployeeShift(selectedEmployee.id);
        if (shiftData?.shifts?.day_time_deduction) {
          // Get shift details for the OT date specifically
          const otDateObj = new Date(fromDate);
          const shiftForDate = getShiftDetailsForDay(shiftData.shifts.day_time_deduction as unknown as DayTimeDeduction[], otDateObj);
          if (shiftForDate?.break_hrs !== undefined && shiftForDate?.break_hrs !== null) {
            setEmployeeBreakHours(Number(shiftForDate.break_hrs));
          } else {
            setEmployeeBreakHours(null);
          }
        } else {
          setEmployeeBreakHours(null);
        }
      } catch (error) {
        console.warn('Error fetching employee break hours:', error);
        setEmployeeBreakHours(null);
      }
    };

    fetchEmployeeBreakHours();
  }, [selectedEmployee?.id, fromDate]);

  // Check for holidays when date changes (uses prefetched/cached holidays)
  useEffect(() => {
      if (!fromDate || !companyId) {
        setHolidayInfo(null);
        setIsHoliday(false);
        return;
      }
    const match = (companyHolidays as Holiday[]).find(h => h.date === fromDate && (h as Partial<Holiday> & { is_public?: boolean }).is_public !== false);
    if (match) {
      setHolidayInfo(match);
        setIsHoliday(true);
      } else {
        setHolidayInfo(null);
        setIsHoliday(false);
      }
  }, [fromDate, companyId, companyHolidays]);

  // Fetch daily overtime records directly
  const { data: overtimeRecordsResponse } = useDailyOvertimeRecords({
    companyId: companyId || '',
    enabled: !!companyId
  });
  
  // Calculate monthly totals from the daily records
  const overtimeStats = useMemo(() => {
    const stats: Record<string, number> = {};
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    // If no data, return empty stats
    if (!overtimeRecordsResponse?.data) {
      return stats;
    }
    
    // Process all dates in the response
    const dailyRecords = overtimeRecordsResponse.data as Record<string, Array<{
      employee_id: string;
      hours: number;
      status: string;
    }>>;
    
    Object.entries(dailyRecords).forEach(([date, records]) => {
      // Only include records from the current month
      const recordDate = new Date(date);
      if (recordDate.getMonth() === currentMonth && recordDate.getFullYear() === currentYear) {
        // Add up hours for each employee
        records.forEach(record => {
          if (record.status === OvertimeStatus.APPROVED) {
            const employeeId = record.employee_id;
            stats[employeeId] = (stats[employeeId] || 0) + Number(record.hours || 0);
          }
        });
      }
    });
    
    return stats;
  }, [overtimeRecordsResponse]);

  // Real-time total hours calculation with validation
  const totalHoursData = useMemo(() => {
    if (!startTime || !endTime) {
      return { hours: null, error: null, isValid: false };
    }

    try {
      const validation = validateTimeDuration(startTime, endTime);
      
      if (!validation.isValid) {
        return { 
          hours: null, 
          error: validation.error || 'Invalid time range', 
          isValid: false 
        };
      }

      return { 
        hours: validation.hours || 0, 
        error: null, 
        isValid: true 
      };
    } catch {
      return { 
        hours: null, 
        error: 'Error calculating hours', 
        isValid: false 
      };
    }
  }, [startTime, endTime]);

  // Extract values for easy access
  const totalHours = totalHoursData.hours;
  const totalHoursError = totalHoursData.error;
  const totalHoursIsValid = totalHoursData.isValid;

  // Fetch employee shift data for overlap validation
  const { data: employeeShiftData } = useEmployeeShiftData(selectedEmployee?.id || null);

  // Check for shift overlap with employee's regular shift
  const shiftOverlapData = useMemo(() => {
    if (!selectedEmployee?.id || !fromDate || !startTime || !endTime || !employeeShiftData?.shifts?.day_time_deduction) {
      return { hasOverlap: false, error: null, shiftTimes: null };
    }

    // Skip shift overlap validation on holidays - all hours are overtime
    if (isHoliday) {
      return { hasOverlap: false, error: null, shiftTimes: null };
    }

    try {
      // Get shift details for the OT date
      const otDateObj = new Date(fromDate);
      const shiftDetailsForDay = getShiftDetailsForDay(employeeShiftData.shifts.day_time_deduction as unknown as DayTimeDeduction[], otDateObj);
      
      if (!shiftDetailsForDay) {
        // No shift schedule for this day, no overlap possible
        return { hasOverlap: false, error: null };
      }

      // Check for overlap using our utility function with in_working_hours logic
      const overlapValidation = validateOvertimeShiftOverlap(
        startTime,
        endTime,
        shiftDetailsForDay.start_time,
        shiftDetailsForDay.end_time,
        employeeShiftData.shifts.in_working_hours || false
      );

      return {
        hasOverlap: overlapValidation.hasOverlap,
        error: overlapValidation.error || null,
        shiftTimes: {
          start: shiftDetailsForDay.start_time,
          end: shiftDetailsForDay.end_time
        }
      };
    } catch (error) {
      console.warn('Error checking shift overlap:', error);
      return { hasOverlap: false, error: null };
    }
  }, [selectedEmployee, fromDate, startTime, endTime, employeeShiftData?.shifts?.day_time_deduction, employeeShiftData?.shifts?.in_working_hours, isHoliday]);

  // Extract overlap validation values for easy access
  const shiftOverlapError = shiftOverlapData.error;
  const hasShiftOverlap = shiftOverlapData.hasOverlap;

  // Check if selected employee is over max hours
  const overMax = selectedEmployee && totalHours && overtimeStats 
    ? (overtimeStats[selectedEmployee.id] || 0) + totalHours > (companyThreshold || 104)
    : false;

  // Show a non-blocking notice if total daily OT (approved + current) reaches 8h and break time will be deducted.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Preconditions for showing the notice
      if (!selectedEmployee?.id || !fromDate || !totalHours || hasShiftOverlap) {
        if (!cancelled) {
          setWillDeductBreak(false);
          setBreakDeductionNotice(null);
        }
        return;
      }

      try {
        // Get break hours from either assigned shift or selected shift from dropdown
        let breakHours = employeeBreakHours;
        
        // If no assigned shift break hours, check if we have selectedShiftData (from dropdown)
        if (!breakHours && selectedShiftData?.day_time_deduction) {
          const otDateObj = new Date(fromDate);
          const shiftForDate = getShiftDetailsForDay(selectedShiftData.day_time_deduction as DayTimeDeduction[], otDateObj);
          if (shiftForDate?.break_hrs !== undefined && shiftForDate?.break_hrs !== null) {
            breakHours = Number(shiftForDate.break_hrs);
          }
        }

        // If still no break hours, no deduction possible
        if (!breakHours) {
          if (!cancelled) {
            setWillDeductBreak(false);
            setBreakDeductionNotice(null);
          }
          return;
        }

        const approvedHours = await getApprovedOvertimeHoursForDate(selectedEmployee.id, fromDate);
        const totalDayHours = approvedHours + totalHours;
        if (!cancelled && totalDayHours >= 8) {
          setWillDeductBreak(true);
          setBreakDeductionNotice(`${fromDate} overtime is more than 8 hours so break time will be deducted.`);
        } else if (!cancelled) {
          setWillDeductBreak(false);
          setBreakDeductionNotice(null);
        }
      } catch {
        if (!cancelled) {
          setWillDeductBreak(false);
          setBreakDeductionNotice(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedEmployee?.id, fromDate, totalHours, employeeBreakHours, selectedShiftData, isHoliday, hasShiftOverlap]);

  // Helper to get justification label
  const justificationLabel = useMemo(() => {
    if (!justification) return '-';
    const found = overtimeReasons.find(r => r.id === justification);
    return found ? found.reason : justification;
  }, [justification, overtimeReasons]);

  // Reset form values
  const resetForm = useCallback(() => {
    setSelectedEmployee(null);
    setFromDate('');
    setStartTime('');
    setEndTime('');
    setJustification('');
    setError(null);
    setConfirmationError(null);
    setDailyOvertimeError(null);
    setEmployeeBreakHours(null);
    setWillDeductBreak(false);
    setBreakDeductionNotice(null);
    setHolidayInfo(null);
    setIsHoliday(false);
    setSubmitting(false);
    setConfirmationOpen(false);
    setPendingSubmitEvent(null);
  }, []);

  // Handle break deduction cancellation
  const handleBreakDeductionCancel = useCallback(() => {
    setShowBreakDeductionModal(false);
    setBreakDeductionData(null);
  }, []);

  // Helper functions (removed unused helpers)

  const getFormattedDateISO = useCallback((date: unknown) => {
    // First, ensure we're working with a string
    if (typeof date !== 'string') {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    // Validate the date format strictly (YYYY-MM-DD)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    // Validate that the date components are valid numbers
    const [year, month, day] = date.split('-').map(Number);
    if (year < 2020 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) {
      const today = new Date();
      const todayYear = today.getFullYear();
      const todayMonth = String(today.getMonth() + 1).padStart(2, '0');
      const todayDay = String(today.getDate()).padStart(2, '0');
      return `${todayYear}-${todayMonth}-${todayDay}`;
    }
    
    // Return the date string as-is without any timezone conversion
    // The date validation in handleSubmit will check if it's within allowed range
    return date;
  }, []);

  // Real-time daily overtime validation (simplified - remove conflicting shift validation)
  useEffect(() => {
    if (!selectedEmployee || !fromDate || !startTime || !endTime) {
      setDailyOvertimeError(null);
      return;
    }
    
    let cancelled = false;
    
    (async () => {
      // Just do basic time validation without shift overlap (we handle that separately now)
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [endHour, endMin] = endTime.split(':').map(Number);

      // Validate time format
      if (
        isNaN(startHour) || isNaN(startMin) || isNaN(endHour) || isNaN(endMin) ||
        startHour < 0 || startHour > 23 || endHour < 0 || endHour > 23 ||
        startMin < 0 || startMin > 59 || endMin < 0 || endMin > 59
      ) {
        setDailyOvertimeError('Invalid time format');
        return;
      }
      
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      // Calculate total minutes accounting for overnight shifts
      const totalMinutes = endMinutes >= startMinutes
        ? endMinutes - startMinutes  // Same day
        : (24 * 60 - startMinutes) + endMinutes;  // Overnight shift

      // Validate minimum duration (30 minutes)
      if (totalMinutes < 30) {
        setDailyOvertimeError('Minimum overtime duration is 30 minutes');
        return;
      }
      
      // Validate maximum duration (24 hours)
      if (totalMinutes > 24 * 60) {
        setDailyOvertimeError('Maximum overtime duration is 24 hours');
        return;
      }
      
      if (cancelled) return;
      
      // Clear any previous errors since basic validation passed
        setDailyOvertimeError(null);
    })();
    
    return () => { cancelled = true; };
  }, [selectedEmployee, fromDate, startTime, endTime, companyId]);

  // Submit handler - optimized for instant modal closing
  const handleSubmit = useCallback(async (
    e?: React.FormEvent,
    options?: { skipBreakTimeCheck?: boolean; adjustedHours?: number; shiftDataLoaded?: boolean }
  ) => {
    if (e) e.preventDefault();
    
    // Clear error immediately for better UX
    setError(null);
    
    // Block if error is present
    if (dailyOvertimeError) {
      setError(dailyOvertimeError);
      return;
    }

    // Block if shift data is still loading (for better UX)
    if (options?.shiftDataLoaded === false && selectedEmployee && fromDate) {
      setError("Please wait for shift information to load before submitting");
      return;
    }
    
    // ✅ NEW: Declare newRequest variable for error handling  
    let newRequest: OvertimeRequest | null = null;
    
    try {
      const hasValidJustification = justification.trim();
      
      // Enhanced mandatory field validation
      if (!selectedEmployee) {
        setError("Please select an employee");
        return;
      }
      
      if (!fromDate) {
        setError("Please select an overtime date");
        return;
      }
      
      if (!startTime) {
        setError("Please enter start time");
        return;
      }
      
      if (!endTime) {
        setError("Please enter end time");
        return;
      }
      
      if (!hasValidJustification) {
        setError("Please select a justification reason");
        return;
      }
      
      // Check if shift selection is required for employees without assigned shifts
      if (selectedShiftData === undefined) {
        setError("Please wait for shift information to load");
        return;
      }
      
      // FIXED: Only require shift selection if employee has NO assigned shift
      const hasAssignedShift = !!employeeShiftData?.id;
      const hasDropdownShift = !!selectedShiftData?.id;
      
      if (!hasAssignedShift && !hasDropdownShift && options?.shiftDataLoaded === true) {
        setError("Please select a shift from the dropdowns above");
        return;
      }
      
      // Use the calculated hours directly from our new implementation
      const adjustedHours = options?.adjustedHours ?? totalHours ?? 0;
        
      // Check break deduction requirement (non-blocking notice already shown; only block if final becomes invalid)
      try {
        const approvedHours = await getApprovedOvertimeHoursForDate(selectedEmployee.id, fromDate);
        const totalDayHours = approvedHours + adjustedHours;
        if (totalDayHours >= 8 && employeeBreakHours && employeeBreakHours > 0) {
          const finalHours = adjustedHours - employeeBreakHours;
          if (finalHours <= 0) {
            setError(`Cannot submit overtime request. After break deduction of ${employeeBreakHours}h, payable hours would be ${finalHours}h or less.`);
            return;
          }
          // Otherwise proceed; backend RPC will apply deduction.
        }
      } catch (error) {
        console.error('Error checking break deduction requirements:', error);
        // Proceed with submission even if this check fails
      }

      // Time validation
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [endHour, endMin] = endTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      
      if (startMinutes === endMinutes || (endMinutes > startMinutes && endMinutes - startMinutes < 30)) {
        setError("Minimum overtime duration is 30 minutes");
        return;
      }

      // Validate hours are within bounds
      if (isNaN(adjustedHours) || adjustedHours <= 0 || adjustedHours > 24) {
        setError("Invalid overtime hours (must be between 0 and 24)");
        return;
      }

      // Date validation
      const selectedDate = new Date(fromDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const PAST_DATE_LIMIT_DAYS = 5;
      const minAllowedDate = new Date(today);
      minAllowedDate.setDate(today.getDate() - PAST_DATE_LIMIT_DAYS);
      
      // Today validation - ensure start time hasn't passed
      if (selectedDate.getTime() === today.getTime()) {
        const now = new Date();
        const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();
        
        if (startMinutes < currentTimeInMinutes) {
          setError("Cannot create overtime request for a time that has already passed");
          return;
        }
      } else if (selectedDate >= minAllowedDate && selectedDate < today) {
        // Past dates within limit are acceptable
      } else if (selectedDate < minAllowedDate) {
        setError(`Cannot create overtime request for dates more than ${PAST_DATE_LIMIT_DAYS} days in the past`);
        return;
      }

      // Fast UX: skip setting submitting to avoid spinner delay
      
      // Format data for submission
      const formattedDateISO = getFormattedDateISO(fromDate);
      const employeeName = `${selectedEmployee.firstName || ''} ${selectedEmployee.lastName || ''}`.trim();
      
      // Get shift assignment ID from employee shift data
      const shiftAssignmentId = employeeShiftData?.id || null;
      
      // Prepare justification ID
      const justificationId = justification;
      
      // ✅ NEW: Create new request object for instant state update
      newRequest = {
        id: uuidv4(), // Generate temporary ID
        employee_id: selectedEmployee.id,
        employee_name: employeeName,
        department: Array.isArray(selectedEmployee.department) 
          ? selectedEmployee.department[0] || ''
          : selectedEmployee.department || '',
        date: formattedDateISO,
        hours: adjustedHours,
        start_time: startTime,
        end_time: endTime,
        justification: justificationId,
        status: user?.role === UserRole.SUPER_ADMIN ? OvertimeStatus.APPROVED : 
                (user?.role === UserRole.SUPERINTENDENT ? 
                  (overMax ? OvertimeStatus.PENDING_VP_APPROVAL : OvertimeStatus.PENDING_DH_APPROVAL) : OvertimeStatus.PENDING),
        submitted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        vp_approval_required: overMax,
        approver_id: user?.role === UserRole.SUPER_ADMIN ? user?.id : null,
        approver_name: user?.role === UserRole.SUPER_ADMIN ? (user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim()) : null,
        approver_comment: user?.role === UserRole.SUPER_ADMIN ? 'Approved' : null,
        created_by_name: user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || null,
        created_by_role: user?.role || null
      };

      // ✅ NEW: Add to local state immediately for instant UI update
      addOvertimeRequestToState(newRequest);

      // Use the new RPC function instead of direct table insert
      const { data: rpcData, error: rpcError } = await supabase.rpc('add_employee_overtime_unified', {
        employee_id_param: selectedEmployee.id,
        employee_name_param: employeeName,
        department_param: Array.isArray(selectedEmployee.department) 
          ? selectedEmployee.department[0] || ''
          : selectedEmployee.department || '',
        date_param: formattedDateISO,
        hours_param: adjustedHours,
        start_time_param: startTime,
        end_time_param: endTime,
        justification_param: justificationId,
        created_by_id_param: user?.id || null,
        created_by_name_param: user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || null,
        created_by_role_param: user?.role || null,
        status_param: overMax ? OvertimeStatus.PENDING_VP_APPROVAL :
                      (user?.role === UserRole.SUPER_ADMIN ? OvertimeStatus.APPROVED : 
                       user?.role === UserRole.SUPERINTENDENT ? OvertimeStatus.PENDING_DH_APPROVAL : OvertimeStatus.PENDING),
        approver_id_param: user?.role === UserRole.SUPER_ADMIN ? user?.id : null,
        approver_name_param: user?.role === UserRole.SUPER_ADMIN ? (user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim()) : null,
        approver_comment_param: user?.role === UserRole.SUPER_ADMIN ? 'Approved' : null,
        // FIXED: Pass only ONE shift source - prioritize assigned shift over dropdown
        shift_assignment_id_param: shiftAssignmentId || null,
        shift_id_param: shiftAssignmentId ? null : (selectedShiftData?.id || null)
      });

      if (rpcError) {
        // ✅ NEW: Rollback local state on RPC error
        removeOvertimeRequestFromState(newRequest.id);
        
        // Handle structured errors from the RPC function
        if (rpcData && (rpcData as any).error) {
          setError(`${(rpcData as any).error}${(rpcData as any).details ? ` - ${(rpcData as any).details}` : ''}`);
        } else {
          setError(`Failed to submit overtime request: ${rpcError.message}`);
        }
        return;
      }

      // Check if RPC returned an error object
      if (rpcData && (rpcData as any).error) {
        // ✅ NEW: Rollback local state on RPC error
        removeOvertimeRequestFromState(newRequest.id);
        setError(`${(rpcData as any).error}${(rpcData as any).details ? ` - ${(rpcData as any).details}` : ''}`);
        return;
      }
      
      // ✅ NEW: Update the request with real ID from RPC response
      if (rpcData && (rpcData as any).id) {
        const updatedRequest = { ...newRequest, id: (rpcData as any).id };
        updateOvertimeRequestInState(updatedRequest);
      }

      // ✅ REMOVED: Duplicate increment call - now handled in OvertimeProvider
      // The dashboard total requests count is automatically incremented when 
      // addOvertimeRequestToState is called above

      // Success handling - optimized for instant modal closing
      // Call onSuccess immediately for instant modal closing
      if (onSuccess) onSuccess();
      
      // Move all heavy operations to happen after modal closes for better UX
      setTimeout(() => {
        // Show success message with break deduction details if applicable
        if (rpcData && (rpcData as any).break_deducted) {
          sonnerToast(`Overtime request submitted successfully! Break time of ${(rpcData as any).break_hours_applied}h was deducted. Final hours: ${(rpcData as any).hours_final}h`);
        } else {
          sonnerToast("Overtime request submitted successfully");
        }

        // Warning for threshold exceeded
        if (overMax) {
          sonnerToast(
              user?.role !== UserRole.SUPER_ADMIN 
                ? 'VP Approval Required - Exceeds threshold' 
                : 'Auto-approved (Super Admin)'
          );
        }
        
        // Reset form after success callback to prevent state conflicts
        resetForm();
      }, 10);
    } catch (err: unknown) {
      // ✅ NEW: Rollback local state on any error
      if (newRequest) {
        removeOvertimeRequestFromState(newRequest.id);
      }
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit request';
      setError(errorMessage);
      sonnerToast("Error: Failed to submit overtime request");
    } finally {
      // Keep UI snappy; avoid toggling submitting here
    }
  }, [
    totalHours,
    justification, 
    dailyOvertimeError, 
    fromDate, 
    getFormattedDateISO, 
    onSuccess, 
    overMax, 
    resetForm, 
    selectedEmployee, 
    startTime, 
    endTime, 
    user,
    employeeBreakHours,
    employeeShiftData?.id,
    selectedShiftData,
    addOvertimeRequestToState,
    updateOvertimeRequestInState,
    removeOvertimeRequestFromState
  ]);

  // Now define the break deduction confirmation handler with proper handleSubmit reference
  const handleBreakDeductionConfirmFinal = useCallback(() => {
    if (breakDeductionData) {
      setShowBreakDeductionModal(false);
      // Re-submit with the adjusted hours and skip break check
      handleSubmit(undefined, { 
        skipBreakTimeCheck: true, 
        adjustedHours: breakDeductionData.finalHours 
      });
    }
  }, [breakDeductionData, handleSubmit]);

  return {
    // Form state
    selectedEmployee,
    setSelectedEmployee,
    fromDate,
    setFromDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    justification,
    setJustification,
    error,
    setError,
    confirmationError,
    setConfirmationError,
    submitting,
    setSubmitting,
    confirmationOpen,
    setConfirmationOpen,
    pendingSubmitEvent,
    setPendingSubmitEvent,
    dailyOvertimeError,
    overMax,
    totalHours,
    totalHoursError,
    totalHoursIsValid,
    shiftOverlapError,
    hasShiftOverlap,
    justificationLabel,
    overtimeReasons,
    overtimeStats,
    employeeBreakHours,
    // Break deduction modal state
    showBreakDeductionModal,
    breakDeductionData,
    // Holiday information
    holidayInfo,
    isHoliday,
    // Break deduction notice
    willDeductBreak,
    breakDeductionNotice,
    // Functions
    resetForm,
    handleSubmit,
    handleBreakDeductionConfirm: handleBreakDeductionConfirmFinal,
    handleBreakDeductionCancel
  };
} 